import { Box, Grid, Stack } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import uploadFileImage from "../../asset/upload_file.svg";
import CloseIcon from "@mui/icons-material/Close";
import LinearProgress from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import { convertToArrayBuffer, parseCXFData } from "../../utils/xmlParser";

const CustomLinearProgress = styled(LinearProgress)(({ theme }) => ({
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#2db300", // Change color to green
  },
  height: "4px", // Adjust height (size)
  borderRadius: "2px",
}));

// this will create a area where we can drop the the file
export const DropzoneArea = ({
  fileName,
  handleUploadFile,
  handleRemoveFile,
  noOfColorPoints = 0,
}) => {
  //  this is general function from library that will take about file handling
  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0); // State for progress
  const [isProgressVisible, setIsProgressVisible] = useState(true); // State for progress

  const onDrop = useCallback(async (acceptedFiles) => {
    setErrorMessage(null);
    setUploadProgress(0); // Reset progress on new file drop
    setIsProgressVisible(true);

    if (acceptedFiles[0] instanceof File) {
      let selectedFile = acceptedFiles[0];
      try {
        const arrayBuffer = await convertToArrayBuffer(selectedFile);
        let xmlJson = parseCXFData(arrayBuffer);
        // Simulate upload progress (replace with your actual upload logic)
        const simulateUpload = async () => {
          for (let i = 1; i <= 100; i++) {
            await new Promise((resolve) => setTimeout(resolve, 20)); // Adjust delay as needed
            setUploadProgress(i);
          }
        };
        await simulateUpload(); // Start simulated upload
        handleUploadFile(xmlJson, selectedFile.name);
      } catch (error) {
        setErrorMessage("Error processing file.");
        setUploadProgress(0); // Reset progress on error
      }
    } else {
      handleRemoveFile();
      setErrorMessage("Invalid file format.");
      setUploadProgress(0); // Reset progress on error
      //Disable the upload button here
    }
  }, []);
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      "application/xml": [".cxf"],
    },
  });

  const handleRemoveProgressBar = () => {
    setIsProgressVisible(false);
  };

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          color: "#303C43",
          fontSize: "22px",
          fontFamily: "Source Sans Pro",
          fontWeight: "600",
          lineHeight: "32px",
          wordWrap: "break-word",
          mb: "24px",
        }}
      >
        Upload File
      </Box>
      <div {...getRootProps({ className: "dropzone" })}>
        <Box
          sx={{
            width: "93%",
            height: "100%",
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "24px",
            paddingBottom: "24px",
            background: "white",
            borderRadius: "4px",
            overflow: "hidden",
            border: "1px #C7C9C7 dotted",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            display: "inline-flex",
          }}
        >
          <input {...getInputProps()} />
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              gap: 10,
            }}
          >
            <Box
              sx={{
                width: "40px",
                height: "40px",
                background: "rgba(55, 147, 213, 0.12)",
                borderRadius: 9999,
                margin: "auto",
              }}
            >
              <Box
                sx={{
                  marginLeft: "12px",
                  marginTop: "10px",
                  cursor: "pointer",
                }}
              >
                {" "}
                <img src={uploadFileImage} />
              </Box>
            </Box>
          </Box>
          <p>
            <span
              style={{
                color: "#385DAE",
                fontSize: 16,
                fontFamily: "Source Sans Pro",
                fontWeight: "400",
                lineHeight: "24px",
                wordWrap: "break-word",
                cursor: "pointer",
              }}
            >
              Click to Upload
            </span>{" "}
            <span className="font-family"> or Drag and drop</span> <br />
            <span
              style={{
                textAlign: "center",
                color: "#858585",
                fontSize: "14px",
                fontFamily: "Source Sans Pro",
                fontWeight: "400",
                lineHeight: "24px",
                wordWrap: "break-word",
                marginLeft: "60px",
              }}
            >
              CXF (max. 500KB)
            </span>
          </p>
          <Box>
            {errorMessage && (
              <div className="error-message">
                <p style={{ color: "red" }}>{errorMessage}</p>
              </div>
            )}
          </Box>
        </Box>
      </div>

      <Box
        sx={{
          width: "100%",
          color: "#303C43",
          fontSize: "16px",
          fontFamily: "Source Sans Pro",
          fontWeight: "400",
          lineHeight: "24px",
          wordWrap: "break-word",
          mt: 2,
        }}
      >
        Max. 1 file,{" "}
        {noOfColorPoints >= 0 ? "up to 100 colors" : "up to 1 resin"}
      </Box>
      <Box sx={{ mt: 1 }}>
        {fileName ? (
          <Grid container sx={{ mt: "26px", mb: 1.5 }}>
            <Grid xs={1}>
              {" "}
              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  background: "rgba(55, 147, 213, 0.12)",
                  borderRadius: 9999,
                  margin: "auto",
                }}
                className="font-family"
              >
                <Box sx={{ marginLeft: "12px", marginTop: "-9px" }}>
                  {" "}
                  <img src={uploadFileImage} style={{ marginTop: "10px" }} />
                </Box>
              </Box>
            </Grid>
            <Grid xs={10}>
              <Box>
                <span> &nbsp;{fileName} </span>
                <br />
                {noOfColorPoints >= 0 ? (
                  <span style={{ fontSize: "14px" }}>
                    &nbsp; {noOfColorPoints} Colors
                  </span>
                ) : (
                  ""
                )}
              </Box>{" "}
            </Grid>
            <Grid xs={1}>
              <CloseIcon
                sx={{ color: "darkgrey", cursor: "pointer" }}
                onClick={() => {
                  handleRemoveFile();
                  handleRemoveProgressBar();
                }}
              />{" "}
            </Grid>
          </Grid>
        ) : (
          ""
        )}

        {uploadProgress > 0 && isProgressVisible && (
          <CustomLinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ mt: 0, width: "96%" }}
          />
        )}
      </Box>
    </Box>
  );
};
